import { createClient } from '@connectrpc/connect';
import { FamilyService } from './gen/family_service_pb';
import { getGrpcTransport } from './transport';
import { parseAuthoritativeModelVersion } from './requestedParameterIntent';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';

export type FamilyScopeRequirementTargetKind =
  | 'editableFamilyCategories'
  | 'familyObjectTypes';

/** Plain (non-proto) shape of one FamilyScopeRequirementInput. */
export interface FamilyScopeRequirementAssessmentInput {
  key: string;
  parameterName: string;
  destination: string;
  bindingKind: string;
  targetKind: FamilyScopeRequirementTargetKind;
  categories: string[];
  objectTypes: string[];
  discriminatorParameterName: string;
  discriminatorDestination: string;
  discriminatorBindingKind: string;
  emptyMatchPolicy: string;
  dataTypeHint: string;
}

export type FamilyScopeAssessmentStatus =
  | 'satisfied'
  | 'missing'
  | 'conflict'
  | 'not-applicable';

export interface FamilyScopeAssessment {
  requirementKey: string;
  satisfied: boolean;
  status: FamilyScopeAssessmentStatus;
  targetKind: string;
  matchingEntityCount: number;
  conflicts: Array<{
    entityId: string;
    entityName: string;
    actualBindingKind: 'type' | 'instance' | null;
    reason: 'binding' | 'dataType';
  }>;
}

export interface FamilyRequirementAssessmentResult {
  success: boolean;
  error?: string;
  modelVersion?: number;
  assessments?: FamilyScopeAssessment[];
}

function getClient() {
  const transport = getGrpcTransport();
  return transport ? createClient(FamilyService, transport) : null;
}

const ASSESSMENT_STATUSES: ReadonlySet<string> = new Set([
  'satisfied',
  'missing',
  'conflict',
  'not-applicable',
]);

function normalizeStatus(value: unknown, satisfied: boolean): FamilyScopeAssessmentStatus {
  const status = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (ASSESSMENT_STATUSES.has(status)
    ? status
    : satisfied
      ? 'satisfied'
      : 'missing') as FamilyScopeAssessmentStatus;
}

function normalizeBindingKind(value: unknown): 'type' | 'instance' | null {
  const kind = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return kind === 'type' || kind === 'instance' ? kind : null;
}

function mapAssessment(raw: any): FamilyScopeAssessment {
  const satisfied = Boolean(raw?.satisfied);
  return {
    requirementKey: typeof raw?.requirementKey === 'string' ? raw.requirementKey : '',
    satisfied,
    status: normalizeStatus(raw?.status, satisfied),
    targetKind: typeof raw?.targetKind === 'string' ? raw.targetKind : '',
    matchingEntityCount:
      typeof raw?.matchingEntityCount === 'number' ? raw.matchingEntityCount : 0,
    conflicts: Array.isArray(raw?.conflicts)
      ? raw.conflicts.map((conflict: any) => ({
          entityId: typeof conflict?.entityId === 'string' ? conflict.entityId : '',
          entityName: typeof conflict?.entityName === 'string' ? conflict.entityName : '',
          actualBindingKind: normalizeBindingKind(conflict?.actualBindingKind),
          reason: conflict?.reason === 'dataType' ? ('dataType' as const) : ('binding' as const),
        }))
      : [],
  };
}

/**
 * Backend-authoritative family-scope verdicts for mapping readiness. The FE
 * deliberately does not fall back to the snapshot-DTO matcher when this call
 * fails: a stale host must surface as a blocked readiness error, not as a
 * silently different assessment engine.
 */
export async function assessMappingFamilyRequirementsGrpc(
  requirements: FamilyScopeRequirementAssessmentInput[]
): Promise<FamilyRequirementAssessmentResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore
      .getState()
      .setLastGrpcError('AssessMappingFamilyRequirements: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.assessMappingFamilyRequirements({ requirements });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore
        .getState()
        .setLastGrpcError(`AssessMappingFamilyRequirements: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      modelVersion: parseAuthoritativeModelVersion(response.modelVersion),
      assessments: response.assessments.map(mapAssessment),
      error: response.error || undefined,
    };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'AssessMappingFamilyRequirements gRPC error';
    useIntegrationDiagnosticsStore
      .getState()
      .setLastGrpcError(`AssessMappingFamilyRequirements: ${message}`);
    return { success: false, error: message };
  }
}
